import React from 'react';
import { usePayternal } from 'hooks/usePayternal';

/**
 * Generic drop-in payment checkout component powered by the Payternal Web Payments SDK.
 *
 * Props:
 *   domId              — unique per page instance, e.g. "payment-grn-1234"
 *   paymentInfo        — { amount, formattedAmount, buttonLabel }
 *   userInfo           — { userId, phone, email? }
 *   getToken           — async () => string
 *   onCheckoutRequest  — async (sdkPayload) => { order_id, payments_hash, service_type,
 *                          country_id, payment_method_id, payment_method_type,
 *                          host_redirect_url, amount, ... }
 *                        Return the full checkout object; the hook sends it to the SDK.
 *   onClose            — () => void
 *   onReady            — () => void  (optional)
 *   appearance         — { brand, mode, buttonSize }  (optional)
 *   className          — wrapper div class
 *
 * Example:
 *   <PaymentCheckout
 *     domId={`payment-grn-${grnId}`}
 *     paymentInfo={{ amount: 100, formattedAmount: '₹1', buttonLabel: 'Pay ₹1' }}
 *     userInfo={{ userId: localStorage.getItem('user_id'), phone: localStorage.getItem('phone_number_signin') }}
 *     getToken={() => new Promise((res, rej) => getPaymentToken({ userId }, res, rej))}
 *     onCheckoutRequest={async (sdkPayload) => ({
 *       order_id: order.id,
 *       amount: order.amount,
 *       payments_hash: order.paymentsHash,
 *       service_type: 'DELIVERY',
 *       country_id: 1,
 *       payment_method_id: sdkPayload.payment_method_id,
 *       payment_method_type: sdkPayload.payment_method_type,
 *       host_redirect_url: 'https://hyperpure.com/payment/result',
 *     })}
 *     onClose={() => setShowPayment(false)}
 *   />
 */
export default function PaymentCheckout({
  domId,
  paymentInfo,
  userInfo,
  getToken,
  onCheckoutRequest,
  onClose,
  onReady,
  onMessage,
  appearance,
  className,
}) {
  const { sdkError } = usePayternal({
    domId,
    paymentInfo,
    userInfo,
    getToken,
    onCheckoutRequest,
    onClose,
    onReady,
    onMessage,
    appearance,
  });

  return (
    <div className={className}>
      {sdkError && (
        <p style={{ color: '#e23744', fontSize: 14, margin: 0 }}>
          Payment could not be loaded. Please refresh and try again.
        </p>
      )}
      <div id={domId} />
    </div>
  );
}
